import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/lib/cn";

interface Props {
  page: number;
  totalPages: number;
  totalElements: number;
  size: number;
  onPageChange: (page: number) => void;
}

/** Row height and visible-row count drive the dropdown height — 4 pages visible, rest scrolls. */
const OPTION_HEIGHT = 34;
const VISIBLE_OPTIONS = 4;

function PageSelect({ page, totalPages, onPageChange }: Omit<Props, "totalElements" | "size">) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    width: number;
    openUp: boolean;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const didAlignRef = useRef(false);

  const pages = useMemo(
    () => Array.from({ length: Math.max(0, totalPages) }, (_, i) => i),
    [totalPages],
  );
  const canOpen = totalPages > 1;

  const updatePlacement = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const panelHeight = OPTION_HEIGHT * VISIBLE_OPTIONS + 8;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    // Pagination sits at the bottom of a page, so opening upwards is the norm.
    const openUp = spaceBelow < panelHeight;
    setPlacement({
      top: openUp ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      didAlignRef.current = false;
      return;
    }
    updatePlacement();
    // Scrolling the option list itself must not reposition the panel: that would
    // publish a fresh placement object and re-run the alignment effect below,
    // snapping the list back to the current page on every wheel tick.
    const onScroll = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      updatePlacement();
    };
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, updatePlacement]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /**
   * Align to the current page once per open — with many pages it would otherwise
   * start at page 1, off-screen. Re-running on every render would fight the user's
   * own scrolling, hence the ref latch.
   */
  useEffect(() => {
    if (!open || !placement) {
      return;
    }
    if (didAlignRef.current) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = Math.max(0, (page - Math.floor((VISIBLE_OPTIONS - 1) / 2)) * OPTION_HEIGHT);
    didAlignRef.current = true;
  }, [open, placement, page]);

  // Prev/Next move the page in the parent, which flows straight back into the trigger label.
  const current = totalPages === 0 ? 0 : page + 1;

  if (!canOpen) {
    return (
      <span className="px-2 tabular-nums">
        {current} / {totalPages}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Page ${current} of ${totalPages}. Change page`}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          "flex items-center gap-1 rounded-lg border px-2 py-1 text-sm tabular-nums transition-colors",
          "border-bridge-border/80 bg-white text-bridge-fg hover:border-bridge-accent/40",
          open && "border-bridge-accent ring-2 ring-bridge-accent/25",
        )}
      >
        {current}
        <ChevronDown
          className={cn("w-3.5 h-3.5 text-bridge-muted transition-transform duration-200", open && "rotate-180")}
          aria-hidden
        />
      </button>
      <span className="tabular-nums text-bridge-muted">/ {totalPages}</span>

      {open &&
        placement &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[200] overflow-hidden rounded-lg border border-bridge-border/70 bg-white shadow-lg shadow-slate-200/80"
            style={{
              top: placement.top,
              left: placement.left,
              minWidth: Math.max(placement.width, 96),
              transform: placement.openUp ? "translateY(-100%)" : undefined,
            }}
          >
            <ul
              ref={listRef}
              role="listbox"
              className="overflow-y-auto overscroll-contain py-1"
              style={{ maxHeight: OPTION_HEIGHT * VISIBLE_OPTIONS }}
            >
              {pages.map((p) => {
                const isActive = p === page;
                return (
                  <li key={p} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => {
                        if (!isActive) onPageChange(p);
                        setOpen(false);
                      }}
                      style={{ height: OPTION_HEIGHT }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 text-left text-sm tabular-nums transition-colors",
                        isActive
                          ? "bg-bridge-accent/12 text-bridge-accent font-medium"
                          : "text-bridge-fg hover:bg-bridge-bg",
                      )}
                    >
                      <span className="flex-1">{p + 1}</span>
                      {isActive && <Check className="shrink-0 w-4 h-4 text-bridge-accent" strokeWidth={2.5} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}

export function Pagination({ page, totalPages, totalElements, size, onPageChange }: Props) {
  const start = totalElements === 0 ? 0 : page * size + 1;
  const end = Math.min((page + 1) * size, totalElements);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-bridge-border/60 text-sm text-bridge-muted">
      <span>
        Showing <span className="text-bridge-fg tabular-nums">{start}–{end}</span> of{" "}
        <span className="text-bridge-fg tabular-nums">{totalElements}</span>
      </span>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          <ChevronLeft size={14} /> Prev
        </Button>
        <PageSelect page={page} totalPages={totalPages} onPageChange={onPageChange} />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
        >
          Next <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}
